// src/pages/ndr-management/NDRManagement.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, subMonths, parseISO, formatISO } from "date-fns";
import { toDate, fromZonedTime } from "date-fns-tz";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import NDRSearchForm from "../../components/ndr-management/NDRSearchForm";
import NDRTable from "../../components/ndr-management/NDRTable";
import NDRActionModal from "../../components/ndr-management/NDRActionModal";
import NDRDetailsModal from "../../components/ndr-management/NDRDetailsModal";
import NDRCallRecordingModal from "../../components/ndr-management/NDRCallRecordingModal";

const lottieSrc = "/animations/loader.lottie";

const NDR_STATUS_TABS = [
  { key: "100", label: "Pending Action", badgeColor: "bg-yellow-100 text-yellow-600", showCount: true },
  { key: "200", label: "Initiated Action", badgeColor: "bg-green-100 text-green-600", showCount: true },
  { key: "300", label: "Closed", badgeColor: "bg-gray-100 text-gray-600", showCount: false },
];

export default function NDRManagement() {
  // Default 30-day window in IST
  const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd'T'HH:mm");
  const defaultTo = format(new Date(), "yyyy-MM-dd'T'HH:mm");

  // Filters (stored in IST)
  const [filters, setFilters] = useState({
    orderReferenceNumber: "",
    awbNumber: "",
    ndrStatusCode: "",
    fromDateTime: defaultFrom,
    toDateTime: defaultTo,
  });

  // Pagination & sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("ndr_creation_date_time_utc");
  const [sortDirection, setSortDirection] = useState("DESC");

  // Data + loading/error flags
  const [ndrRecords, setNdrRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);

  // Tab counts
  const [statusCounts, setStatusCounts] = useState(
    Object.fromEntries(NDR_STATUS_TABS.map(t => [t.key, 0]))
  );
  const [selectedTab, setSelectedTab] = useState("100");

  // Modal states
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [callRecordingModalOpen, setCallRecordingModalOpen] = useState(false);
  const [selectedNDR, setSelectedNDR] = useState(null);
  const [selectedActionType, setSelectedActionType] = useState(null);

  useEffect(() => {
    fetchNDRData();
  }, [selectedTab]); // Re-fetch when tab changes

  // Convert IST datetime to UTC
  const convertISTtoUTC = (istDateTimeString) => {
    if (!istDateTimeString) return null;
    try {
      // Parse the IST datetime string and convert to UTC
      const istDate = parseISO(istDateTimeString);
      const utcDate = fromZonedTime(istDate, 'Asia/Kolkata');
      return formatISO(utcDate);
    } catch (error) {
      console.error("Error converting IST to UTC:", error);
      return null;
    }
  };

  const fetchNDRData = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      
      if (!authToken) {
        setError("Authentication token not found");
        setLoading(false);
        return;
      }

      // Convert IST to UTC for API
      const fromUTC = convertISTtoUTC(filters.fromDateTime);
      const toUTC = convertISTtoUTC(filters.toDateTime);

      const body = {
        merchant_id: merchantId,
        order_reference_number: filters.orderReferenceNumber || null,
        awb_number: filters.awbNumber || null,
        ndr_management_status_code: selectedTab, // Always filter by selected tab
        ndr_status_code: filters.ndrStatusCode || null,
        order_creation_date_time_utc_from: fromUTC,
        order_creation_date_time_utc_to: toUTC,
        limit: overrides.limit !== undefined ? overrides.limit : limit,
        skip: overrides.skip !== undefined ? overrides.skip : (currentPage - 1) * limit,
        sort_field: overrides.sortField || sortField,
        sort_direction: overrides.sortDirection || sortDirection,
        fetch_only_count: false,
      };

      // Remove null values
      Object.keys(body).forEach(key => {
        if (body[key] === null || body[key] === "") {
          delete body[key];
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/search`,
        body,
        { 
          headers: { 
            Authorization: authToken, 
            "x-merchant-id": merchantId, 
            "Content-Type": "application/json" 
          } 
        }
      );

      setNdrRecords(response.data.ndr_records || []);
      setTotalRecords(response.data.total_count || 0);

      // Fetch counts for tabs that need counts (only Pending and Initiated Action)
      await fetchStatusCounts(authToken, merchantId);
    } catch (err) {
      console.error("Error fetching NDR data:", err);
      setError(err.response?.data?.message || "Failed to fetch NDR records");
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, limit, sortField, sortDirection, selectedTab]);

  const fetchStatusCounts = async (authToken, merchantId) => {
    try {
      // Convert IST to UTC for API
      const fromUTC = convertISTtoUTC(filters.fromDateTime);
      const toUTC = convertISTtoUTC(filters.toDateTime);

      const counts = {};

      // Fetch count for each status that should show count
      for (const tab of NDR_STATUS_TABS) {
        if (!tab.showCount) {
          // Don't set any count for tabs that shouldn't show count
          continue;
        }

        const body = {
          merchant_id: merchantId,
          ndr_management_status_code: tab.key,
          order_creation_date_time_utc_from: fromUTC,
          order_creation_date_time_utc_to: toUTC,
          fetch_only_count: true,
        };

        Object.keys(body).forEach(key => {
          if (body[key] === null || body[key] === "") {
            delete body[key];
          }
        });

        const response = await axios.post(
          `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/search`,
          body,
          { 
            headers: { 
              Authorization: authToken, 
              "x-merchant-id": merchantId, 
              "Content-Type": "application/json" 
            } 
          }
        );

        counts[tab.key] = response.data.total_count || 0;
      }

      setStatusCounts(counts);
    } catch (err) {
      console.error("Error fetching status counts:", err);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchNDRData({ skip: 0 });
  };

  const handleReset = () => {
    const defaultFrom = format(subMonths(new Date(), 1), "yyyy-MM-dd'T'HH:mm");
    const defaultTo = format(new Date(), "yyyy-MM-dd'T'HH:mm");
    
    setFilters({
      orderReferenceNumber: "",
      awbNumber: "",
      ndrStatusCode: "",
      fromDateTime: defaultFrom,
      toDateTime: defaultTo,
    });
    setCurrentPage(1);
    setSelectedTab("100");
  };

  const handleTabChange = (tabKey) => {
    setSelectedTab(tabKey);
    setCurrentPage(1);
    // Data will be fetched automatically due to useEffect dependency on selectedTab
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    fetchNDRData({ limit: newLimit, skip: 0 });
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      fetchNDRData({ skip: (currentPage - 2) * limit });
    }
  };

  const nextPage = () => {
    if (ndrRecords.length === limit) {
      setCurrentPage(prev => prev + 1);
      fetchNDRData({ skip: currentPage * limit });
    }
  };

  const handleActionClick = (ndr, actionType = null) => {
    setSelectedNDR(ndr);
    setSelectedActionType(actionType);
    setActionModalOpen(true);
  };

  const handleDetailsClick = (ndr) => {
    setSelectedNDR(ndr);
    setDetailsModalOpen(true);
  };

  const handleCallRecordingClick = (ndr) => {
    setSelectedNDR(ndr);
    setCallRecordingModalOpen(true);
  };

  const handleActionSubmit = async (actionData) => {
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");
      
      if (!authToken) {
        throw new Error("Authentication token not found");
      }

      const response = await axios.put(
        `${process.env.REACT_APP_OMS_BASE_URL}/v2/oms/ndr/action-update`,
        actionData,
        { 
          headers: { 
            Authorization: authToken, 
            "x-merchant-id": merchantId, 
            "Content-Type": "application/json" 
          } 
        }
      );

      // Check if response is successful
      if (response.status === 200 || response.status === 201) {
        // Refresh data in the background
        fetchNDRData();
        // Don't close modal here - let the modal show success and then close
        return;
      } else {
        // Unexpected success status code
        throw new Error("Unexpected response from server");
      }
    } catch (err) {
      console.error("Error updating NDR action:", err);
      // Extract error message from response
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.data?.error || 
        err.message || 
        "Failed to update NDR action. Please try again.";
      
      // Throw error so modal can catch it
      throw new Error(errorMessage);
    }
  };

  const totalPages = Math.ceil(totalRecords / limit);

  return (
    <div className="px-3 sm:px-3 lg:px-3 py-3 max-w-[1800px] mx-auto">

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-3 mb-6">
        {NDR_STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              selectedTab === tab.key
                ? "bg-blue-600 text-white shadow-md"
                : "bg-white text-gray-700 border hover:bg-gray-50"
            }`}
          >
            <span>{tab.label}</span>
            {tab.showCount && (
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedTab === tab.key
                    ? "bg-white/20 text-white"
                    : tab.badgeColor
                }`}
              >
                {statusCounts[tab.key] || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search Form */}
      <NDRSearchForm
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <DotLottieReact
            src={lottieSrc}
            loop
            autoplay
            style={{ width: 200, height: 200 }}
          />
          <p className="text-gray-600 mt-4">Loading NDR records...</p>
        </div>
      )}

      {/* Results Summary */}
      {!loading && (
        <div className="mb-4 text-sm text-gray-600">
          Showing {ndrRecords.length} of {totalRecords} records
        </div>
      )}

      {/* Table */}
      {!loading && ndrRecords.length > 0 && (
        <NDRTable
          ndrRecords={ndrRecords}
          onActionClick={handleActionClick}
          onDetailsClick={handleDetailsClick}
          onCallRecordingClick={handleCallRecordingClick}
        />
      )}

      {/* No Results */}
      {!loading && ndrRecords.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg">No NDR records found</p>
          <p className="text-gray-500 text-sm mt-2">
            Try adjusting your search filters
          </p>
        </div>
      )}

      {/* Pagination */}
      {!loading && ndrRecords.length > 0 && (
        <div className="flex flex-wrap justify-between items-center mt-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
            >
              ← Previous
            </button>

            <span className="font-medium">
              Page {currentPage}
              {totalPages > 1 && <span> of {totalPages}</span>}
            </span>

            <button
              onClick={nextPage}
              disabled={ndrRecords.length < limit}
              className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
            >
              Next →
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <label className="font-medium">Rows per page:</label>
            <select
              value={limit}
              onChange={handleLimitChange}
              className="px-3 py-2 border rounded focus:ring-2 focus:ring-blue-400"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModalOpen && selectedNDR && (
        <NDRActionModal
          ndr={selectedNDR}
          preSelectedAction={selectedActionType}
          onClose={() => {
            setActionModalOpen(false);
            setSelectedNDR(null);
            setSelectedActionType(null);
          }}
          onSubmit={handleActionSubmit}
        />
      )}

      {/* Details Modal */}
      {detailsModalOpen && selectedNDR && (
        <NDRDetailsModal
          ndr={selectedNDR}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedNDR(null);
          }}
          onActionClick={handleActionClick}
        />
      )}

      {/* Call Recording Modal */}
      {callRecordingModalOpen && selectedNDR && (
        <NDRCallRecordingModal
          ndr={selectedNDR}
          onClose={() => {
            setCallRecordingModalOpen(false);
            setSelectedNDR(null);
          }}
        />
      )}
    </div>
  );
}